// Utilitário para gerenciar URLs de capas com renovação automática
import axios from '../config/axios';

interface CoverUrlData {
  url: string;
  fileId: number;
  expiresAt?: number;
}

class CoverUrlManager {
  private urlCache = new Map<number, CoverUrlData>();
  private readonly EXPIRY_BUFFER = 24 * 60 * 60 * 1000; // 24 horas antes de expirar

  // Extrai informações da signed URL
  private parseSignedUrl(url: string): { expiresAt?: number } {
    try {
      const urlObj = new URL(url);
      const expires = urlObj.searchParams.get('X-Goog-Expires');
      if (expires) {
        const expiresAt = Date.now() + (parseInt(expires) * 1000);
        return { expiresAt };
      }
    } catch (error) {
      console.error('Erro ao analisar signed URL:', error);
    }
    return {};
  }

  // Verifica se uma URL está próxima de expirar
  private isUrlExpiring(urlData: CoverUrlData): boolean {
    if (!urlData.expiresAt) return false;
    return (urlData.expiresAt - Date.now()) < this.EXPIRY_BUFFER;
  }

  // Renova uma URL de capa
  async refreshCoverUrl(fileId: number): Promise<string> {
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/cover/${fileId}/refresh-url`);
      const newUrl = response.data.data.url;

      // Atualiza o cache
      const urlData: CoverUrlData = {
        url: newUrl,
        fileId,
        ...this.parseSignedUrl(newUrl)
      };

      this.urlCache.set(fileId, urlData);
      return newUrl;
    } catch (error) {
      console.error('Erro ao renovar URL da capa:', error);
      throw error;
    }
  }

  // Obtém URL de capa (renova se necessário)
  async getCoverUrl(fileId: number, currentUrl?: string): Promise<string> {
    const cached = this.urlCache.get(fileId);

    // Se não tem cache ou URL atual é diferente, usa a atual
    if (!cached || (currentUrl && cached.url !== currentUrl)) {
      const urlData: CoverUrlData = {
        url: currentUrl || '',
        fileId,
        ...this.parseSignedUrl(currentUrl || '')
      };
      this.urlCache.set(fileId, urlData);
    }

    const urlData = this.urlCache.get(fileId)!;

    // Se está expirando, renova
    if (this.isUrlExpiring(urlData)) {
      console.log(`Renovando URL da capa ${fileId} - próxima expiração`);
      return await this.refreshCoverUrl(fileId);
    }

    return urlData.url;
  }

  // Limpa cache (útil quando o usuário sai da página)
  clearCache() {
    this.urlCache.clear();
  }
}

export const coverUrlManager = new CoverUrlManager();